/**
 * Persisted collapsed/open state for a funnel pipeline board within one tenant.
 */
export interface PipelineColumnPreference {
  /** Funnel status ids rendered as collapsed rails in the pipeline board. */
  collapsedStatusIds: number[];
}

/**
 * Full browser-local storage payload for Communications pipeline preferences.
 *
 * The data is scoped first by tenant and then by funnel so multiple tenants
 * and funnels can coexist in the same browser safely.
 */
export interface PipelineColumnPreferenceStorage {
  /** Tenant-scoped funnel preferences keyed by tenant id and funnel id. */
  scopes: Record<string, Record<string, PipelineColumnPreference>>;
}

/**
 * Parameters used to resolve a single funnel preference from local storage.
 */
export interface PipelineColumnPreferenceScope {
  /** Tenant scope for the currently active browser session. */
  tenantId: string;
  /** Funnel id for the currently selected Communications board. */
  funnelId: number;
}

/**
 * Result of loading a funnel preference from local storage.
 */
export interface ReadPipelineColumnPreferenceResult {
  /** Persisted preference when one exists for the requested tenant/funnel. */
  preference: PipelineColumnPreference | null;
  /** Whether an explicit preference entry exists for the requested scope. */
  hasStoredPreference: boolean;
}

/**
 * Versioned storage key for Communications pipeline column preferences.
 */
export const PIPELINE_COLUMN_PREFERENCE_STORAGE_KEY =
  'lp.ui.communications.pipelineColumns.v1';

/**
 * Custom event fired after local preference updates in the current tab.
 */
export const PIPELINE_COLUMN_PREFERENCE_CHANGE_EVENT =
  'lp.ui.communications.pipelineColumnsChange';

const createEmptyStorage = (): PipelineColumnPreferenceStorage => ({
  scopes: {},
});

const isValidStatusId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

const normalizeCollapsedStatusIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(value.filter(isValidStatusId).sort((left, right) => left - right))
  );
};

const normalizePreference = (
  value: unknown
): PipelineColumnPreference | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;

  return {
    collapsedStatusIds: normalizeCollapsedStatusIds(
      candidate.collapsedStatusIds
    ),
  };
};

const normalizeStorage = (
  value: unknown
): PipelineColumnPreferenceStorage | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const rawScopes = candidate.scopes;

  if (!rawScopes || typeof rawScopes !== 'object') {
    return null;
  }

  const scopes: PipelineColumnPreferenceStorage['scopes'] = {};

  Object.entries(rawScopes as Record<string, unknown>).forEach(
    ([tenantId, rawFunnels]) => {
      if (!rawFunnels || typeof rawFunnels !== 'object') {
        return;
      }

      const funnels: Record<string, PipelineColumnPreference> = {};

      Object.entries(rawFunnels as Record<string, unknown>).forEach(
        ([funnelId, rawPreference]) => {
          const preference = normalizePreference(rawPreference);

          if (!preference) {
            return;
          }

          funnels[funnelId] = preference;
        }
      );

      if (Object.keys(funnels).length > 0) {
        scopes[tenantId] = funnels;
      }
    }
  );

  return { scopes };
};

/**
 * Safely reads the full Communications pipeline preference storage payload.
 *
 * @returns Normalized storage payload, or an empty storage object when missing
 * or invalid.
 */
export const readPipelineColumnPreferenceStorage =
  (): PipelineColumnPreferenceStorage => {
    if (typeof window === 'undefined') {
      return createEmptyStorage();
    }

    const rawValue = window.localStorage.getItem(
      PIPELINE_COLUMN_PREFERENCE_STORAGE_KEY
    );

    if (!rawValue) {
      return createEmptyStorage();
    }

    try {
      const parsedValue = JSON.parse(rawValue) as unknown;
      return normalizeStorage(parsedValue) ?? createEmptyStorage();
    } catch {
      return createEmptyStorage();
    }
  };

/**
 * Reads the stored preference for one tenant/funnel scope.
 *
 * @param scope - Current tenant and funnel selection.
 * @returns Stored preference and whether it existed explicitly.
 */
export const readPipelineColumnPreference = (
  scope: PipelineColumnPreferenceScope
): ReadPipelineColumnPreferenceResult => {
  const storage = readPipelineColumnPreferenceStorage();
  const funnelKey = String(scope.funnelId);
  const hasTenantPreference = Object.prototype.hasOwnProperty.call(
    storage.scopes,
    scope.tenantId
  );
  const preferenceByTenant = hasTenantPreference
    ? storage.scopes[scope.tenantId]
    : undefined;
  const hasStoredPreference = Boolean(
    preferenceByTenant && funnelKey in preferenceByTenant
  );
  const preference =
    hasStoredPreference && preferenceByTenant
      ? preferenceByTenant[funnelKey]
      : null;

  return {
    preference,
    hasStoredPreference,
  };
};

const writePipelineColumnPreferenceStorage = (
  storage: PipelineColumnPreferenceStorage
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    PIPELINE_COLUMN_PREFERENCE_STORAGE_KEY,
    JSON.stringify(storage)
  );
  window.dispatchEvent(new Event(PIPELINE_COLUMN_PREFERENCE_CHANGE_EVENT));
};

/**
 * Persists the collapsed status ids for one tenant/funnel scope.
 *
 * Empty arrays are stored intentionally so explicit "show all columns"
 * preferences still override the default AI-collapsed behavior.
 *
 * @param scope - Current tenant and funnel selection.
 * @param collapsedStatusIds - Explicit collapsed status ids to persist.
 */
export const persistPipelineColumnPreference = (
  scope: PipelineColumnPreferenceScope,
  collapsedStatusIds: number[]
): void => {
  const storage = readPipelineColumnPreferenceStorage();
  const funnelKey = String(scope.funnelId);
  const normalizedIds = normalizeCollapsedStatusIds(collapsedStatusIds);

  storage.scopes[scope.tenantId] = {
    ...(storage.scopes[scope.tenantId] ?? {}),
    [funnelKey]: {
      collapsedStatusIds: normalizedIds,
    },
  };

  writePipelineColumnPreferenceStorage(storage);
};
